import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

const FONT = {
  body: "'Soehne', sans-serif",
  emphasis: "'Soehne Kraftig', sans-serif",
  mono: "'Soehne Mono', monospace",
} as const

const tokens = {
  dark: {
    border: 'duskBlue.700',
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
    textTertiary: 'text.tertiary',
  },
  light: {
    border: 'light.border.subtle',
    textPrimary: 'light.text.primary',
    textSecondary: 'light.text.secondary',
    textTertiary: 'light.text.tertiary',
  },
} as const

export const steppedListRootStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  w: 'full',
  /**
   * Preserve author-entered whitespace in the step prompt — both the line
   * breaks and any leading/trailing/repeated spaces — while still wrapping
   * long lines. `pre-wrap` (not `pre-line`) is required so the spaces survive.
   *
   * @see https://hivewatch.atlassian.net/browse/ENG-31260
   */
  whiteSpace: 'pre-wrap',
}

export const sectionStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  borderBottom: '1px solid',
  borderBottomColor: tokens[colorScheme].border,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  mb: '12px',
  pb: '12px',
  px: '14px',
  w: 'full',
})

export const sectionMetaRowStyles: SystemStyleObject = {
  alignItems: 'baseline',
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
  w: 'full',
}

export const sectionHeaderStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].textSecondary,
  fontFamily: FONT.body,
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '16px',
  m: 0,
  // Allow the header to shrink and wrap within the meta row instead of being
  // pushed into a narrow column by the non-shrinking (flexShrink: 0) metadata
  // sibling.
  minW: 0,
})

export const sectionMetadataStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].textTertiary,
  flexShrink: 0,
  fontFamily: FONT.mono,
  fontSize: '10px',
  fontWeight: '400',
  lineHeight: '14px',
  m: 0,
  textTransform: 'uppercase',
})

export const sectionContentStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].textPrimary,
  fontFamily: FONT.emphasis,
  fontSize: '16px',
  fontWeight: '500',
  lineHeight: '22px',
  m: 0,
})
